<template>
  <Card
    class="payment-process-tile"
    v-bind="card"
  >
    <template #heading>
      <Heading
        tag="h2"
        size="sm"
        class="payment-process-tile__heading text--left"
        spaces-bottom-rem
      >
        <template v-if="customHeader">{{ customHeader }}</template>
        <template v-else-if="index"> {{ index }}. Zahlung</template>
        <template v-else>Zahlung</template>
        <div
          v-if="providerName"
          class="text--small"
        >
          {{ providerName }}
        </div>
      </Heading>
    </template>

    <template #default>
      <AccordionItem
        v-if="items"
        class="payment-process-tile__accordion"
        :class="{
          'payment-process-tile__accordion--open-mobile': items.length === 1,
        }"
        is-open-on-desktop
        :is-open="items.length === 1"
      >
        <template #title>{{ items.length }} Artikel anzeigen</template>
        <template #content>
          <OrderItems
            :order="{
              items: items,
            }"
            hide-quantity
            hide-provider
          />
        </template>
      </AccordionItem>

      <template v-if="items?.length > 1"> </template>
      <template v-else> </template>
    </template>

    <template #action>
      <template v-if="isSuccessful">
        <div class="payment-process-tile__status payment-process-tile__status--success">
          <SvgIcon
            class="payment-process-tile__status-icon"
            no-icon-class
            :image="CheckIcon"
          />
          Zahlung abgeschlossen
        </div>
      </template>
      <template v-else-if="isError">
        <div class="payment-process-tile__status payment-process-tile__status--error">
          <SvgIcon
            class="payment-process-tile__status-icon"
            no-icon-class
            :image="CloseIcon"
          />
          Zahlung nicht erfolgt
        </div>
      </template>
      <LinkButton
        v-else-if="paymentLink && isPaymentPossible"
        tag="a"
        :href="paymentLink"
        :data-testid="`${provider}-bestaetigung__link`"
        primary
        label="Bezahlen"
      />
      <LinkButton
        v-else
        tag="span"
        primary
        disabled
        label="Bezahlen"
      />
      <LinkButton
        v-if="hasCancel"
        tag="button"
        label="Verwerfen"
        outline
        @click="$emit('cancel')"
      />
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCartStore } from '~/stores/cart';

import { AccordionItem, Heading, Card, LinkButton } from '@i22/td-longtail-vue-components';
import type { CardComponentProps } from '@i22/td-longtail-vue-components/dist/components/card/card.vue';
import type { Item } from '~/lib/types/cartdata';
import CheckIcon from '~/assets/images/ods/circle-checkmark-type-standard.svg';
import CloseIcon from '~/assets/images/ods/circle-close-type-standard.svg';
import { getProvider } from '~/lib/providers';
import OrderItems from '~/components/shop/order-items.vue';

const { cartData, getAnonymousCartData, cartInitialized } = storeToRefs(useCartStore());

export type PaymentProcessTileProps = {
  customItems?: Item[];
  customHeader?: string;
  index?: number;
  provider?: 'BRODOS' | 'TELEKOM';
  isPaymentPossible?: boolean;
  isSuccessful?: boolean;
  isError?: boolean;
  hasCancel?: boolean;
};

const props = withDefaults(defineProps<PaymentProcessTileProps>(), {
  index: undefined,
  provider: undefined,
  customItems: undefined,
  customHeader: undefined,
});

defineEmits<{
  (e: 'cancel'): void;
}>();

const paymentLink = computed(() => {
  if (props.provider === 'BRODOS') {
    return cartData.value?.paymentLinkBrodos;
  }
  if (props.provider === 'TELEKOM') {
    return cartData.value?.paymentLinkTelekom;
  }
  return undefined;
});

const items = computed<Item[] | undefined>(() => {
  if (props.customItems) return props.customItems;
  if (!cartData.value?.items) return [];
  if (props.provider === 'BRODOS') {
    return cartData.value.items.filter(({ provider }) => provider === 'BRODOS');
  }
  if (props.provider === 'TELEKOM') {
    return cartData.value.items.filter(({ provider }) => provider === 'TELEKOM');
  }

  return cartData.value.items;
});

const providerName = computed(() => getProvider(props.provider));

const card = computed<CardComponentProps>(() => ({
  size: 'small',
  // heading: {
  //   tag: 'h2',
  //   size: 'md',
  //   text: props.index ? `${props.index}. Zahlung: <span></span>` : 'Zahlung',
  //   spacesBottomRem: true,
  // },
}));
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.payment-process-tile {
  max-width: 600px;

  --order-item-image-xs-width: 80px;

  &__heading {
    @include mq($mq-md) {
      text-align: center;
    }
  }

  &__accordion {
    margin-inline: $grid-base * -1;
    @include mq($mq-md) {
      margin-inline: auto;

      :deep(.accordion-item__title) {
        display: none;
      }
    }

    &--open-mobile {
      :deep(.accordion-item__title) {
        display: none;
      }
    }
  }

  &__status {
    width: 100%;
    text-align: center;

    font-weight: 700;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;

    &-icon {
      width: 3rem;
      pointer-events: none;
      fill: currentColor;
      --color-border: currentColor;
    }

    &--success {
      color: var(--color-success);
      &-icon {
        color: var(--color-success);
      }
    }

    &--error {
      color: var(--color-error);
      &-icon {
        color: var(--color-error);
      }
    }
  }
}
</style>
